import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LOOKBOOK_DATA, LookbookItem } from "../data/lookbook.mock";
import grain from "../assets/grain.png";

type Chapter = {
  no: number;
  title: string;
  subtitle: string;
};

const OWL_CHAPTERS: Omit<Chapter, "no">[] = [
  {
    title: "SILENCE, CUT INTO FABRIC",
    subtitle: "Luxury streetwear editorial for night thinkers.",
  },
  {
    title: "THE CITY DOESN’T SLEEP",
    subtitle: "Soft gold. Hard edges. Quiet confidence.",
  },
  {
    title: "AFTER MIDNIGHT UNIFORM",
    subtitle: "Minimal form — maximum presence.",
  },
  {
    title: "DREAMS WITH TEETH",
    subtitle: "A calm rebellion, worn like a whisper.",
  },
];

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(!!mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);
  return reduced;
}

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

export default function LookbookLuxuryOwlDream() {
  const navigate = useNavigate();
  const reducedMotion = usePrefersReducedMotion();

  const items = useMemo(() => LOOKBOOK_DATA, []);
  const [active, setActive] = useState(0);

  // modal uses index instead of object (easier prev/next)
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  // loader
  const [loading, setLoading] = useState(true);

  // image fit toggle (đỡ cắt)
  const [fitMode, setFitMode] = useState<"contain" | "cover">("contain");

  // mobile index drawer
  const [showIndex, setShowIndex] = useState(false);

  const sectionsRef = useRef<(HTMLElement | null)[]>([]);
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  /* ================= LOADER 0.7s ================= */
  useEffect(() => {
    if (reducedMotion) {
      setLoading(false);
      return;
    }
    const t = window.setTimeout(() => setLoading(false), 700);
    return () => window.clearTimeout(t);
  }, [reducedMotion]);

  /* ================= ACTIVE SCENE (Intersection) ================= */
  useEffect(() => {
    const els = sectionsRef.current.filter(Boolean) as HTMLElement[];
    if (!els.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        const best = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => (b.intersectionRatio ?? 0) - (a.intersectionRatio ?? 0))[0];

        if (!best) return;
        const idx = Number((best.target as HTMLElement).dataset.index);
        if (!Number.isNaN(idx)) setActive(idx);
      },
      { threshold: [0.35, 0.55, 0.75] }
    );

    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [items.length]);

  /* ================= MODAL LOCK + ESC + ARROWS ================= */
  useEffect(() => {
    document.body.style.overflow = openIndex != null ? "hidden" : "";
    if (openIndex != null) setTimeout(() => closeBtnRef.current?.focus(), 0);
    return () => {
      document.body.style.overflow = "";
    };
  }, [openIndex]);

  useEffect(() => {
    if (openIndex == null) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenIndex(null);
      if (e.key === "ArrowRight") setOpenIndex((i) => (i == null ? i : clamp(i + 1, 0, items.length - 1)));
      if (e.key === "ArrowLeft") setOpenIndex((i) => (i == null ? i : clamp(i - 1, 0, items.length - 1)));
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [openIndex, items.length]);

  const scrollTo = (index: number) => {
    const i = clamp(index, 0, items.length - 1);
    sectionsRef.current[i]?.scrollIntoView({
      behavior: reducedMotion ? "auto" : "smooth",
      block: "start",
    });
  };

  const progress = items.length <= 1 ? 0 : active / (items.length - 1);

  const getChapter = (sceneIndex: number): Chapter => {
    const chapNo = Math.floor(sceneIndex / 2) + 1;
    const base = OWL_CHAPTERS[(chapNo - 1) % OWL_CHAPTERS.length];
    return { no: chapNo, ...base };
  };

  const activeChapter = getChapter(active);
  const openItem = openIndex != null ? items[openIndex] : null;

  return (
    <main className="relative bg-brand-ivory text-brand-brown overflow-x-hidden">
      {/* ===== KEYFRAMES ===== */}
      <style>{`
        @keyframes od_loader_fade {
          0% { opacity: 1 }
          85% { opacity: 1 }
          100% { opacity: 0; visibility: hidden; pointer-events: none }
        }
        @keyframes od_title_reveal {
          0% { opacity: 0; transform: translateY(10px); letter-spacing: .12em; }
          100% { opacity: 1; transform: translateY(0px); letter-spacing: .32em; }
        }
        @keyframes od_fade_up {
          0% { opacity: 0; transform: translateY(12px) }
          100% { opacity: 1; transform: translateY(0px) }
        }
        .od-snap {
          scroll-snap-type: y proximity;
        }
        .od-snap > section {
          scroll-snap-align: start;
        }
      `}</style>

      {/* ===== GRAIN (subtle) ===== */}
      <div
        className="pointer-events-none fixed inset-0 z-[5] opacity-[0.02] mix-blend-multiply"
        style={{
          backgroundImage: `url(${grain})`,
          backgroundRepeat: "repeat",
          backgroundSize: "320px 320px",
        }}
      />

      {/* ===== LOADER ===== */}
      {loading && !reducedMotion && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-brand-ivory"
          style={{ animation: "od_loader_fade 0.7s ease forwards" }}
        >
          <div className="text-center px-8">
            <p className="text-[10px] uppercase tracking-[0.6em] text-brand-brownLight">
              Editorial / Lookbook
            </p>

            <h1
              className="mt-6 text-[44px] md:text-[66px] font-serif-lux uppercase text-brand-brown"
              style={{ animation: "od_title_reveal 0.7s ease forwards" }}
            >
              OWL DREAM
            </h1>

            <div className="mx-auto mt-10 h-px w-24 bg-brand-gold/80" />
            <p className="mt-6 text-[10px] uppercase tracking-[0.55em] text-brand-brown/55">
              dream in the dark
            </p>
          </div>
        </div>
      )}

      {/* ===== TOP BAR (new) ===== */}
      <header className="fixed top-0 left-0 right-0 z-30">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 md:px-10 pt-5">
          <div className="rounded-full border border-brand-brown/10 bg-brand-ivory/75 backdrop-blur-md px-5 py-3">
            <div className="flex items-center justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.62em] text-brand-brownLight">
                  Lookbook
                </p>
                <p className="mt-1 hidden md:block text-[10px] uppercase tracking-[0.5em] text-brand-brown/45 truncate">
                  {`Chapter ${pad2(activeChapter.no)} — ${activeChapter.title}`}
                </p>
              </div>

              <div className="flex items-center gap-3">
                {/* fit toggle */}
                <button
                  onClick={() => setFitMode((m) => (m === "contain" ? "cover" : "contain"))}
                  className="h-9 px-4 rounded-full border border-brand-brown/12 hover:border-brand-brown/30 transition
                             text-[10px] uppercase tracking-[0.55em]"
                  title="Toggle image fit"
                >
                  {fitMode === "contain" ? "Fit" : "Fill"}
                </button>

                {/* mobile index */}
                <button
                  onClick={() => setShowIndex(true)}
                  className="md:hidden h-9 px-4 rounded-full bg-brand-brown/5 hover:bg-brand-brown/8 transition
                             text-[10px] uppercase tracking-[0.55em] border border-brand-brown/12"
                >
                  Index
                </button>

                {/* counter + progress */}
                <span className="text-[10px] uppercase tracking-[0.6em] text-brand-brown/55">
                  {pad2(active + 1)} / {pad2(items.length)}
                </span>

                <div className="w-20 sm:w-28 md:w-40 h-[2px] bg-brand-brown/15 rounded-full overflow-hidden">
                  <div className="h-full bg-brand-gold" style={{ width: `${Math.round(progress * 100)}%` }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* ===== STICKY LEFT RAIL (new) — thumbnails ONLY ONCE (fix spam load) ===== */}
      <aside className="hidden md:block fixed left-0 top-0 bottom-0 z-20">
        <div className="h-full flex items-center">
          <div className="ml-6 lg:ml-10 w-[210px]">
            <div className="rounded-[26px] border border-brand-brown/10 bg-brand-ivory/70 backdrop-blur-md p-4">
              <p className="text-[10px] uppercase tracking-[0.62em] text-brand-brownLight">
                Editorial Index
              </p>

              <div className="mt-4 h-px bg-brand-brown/10" />

              <div className="mt-4 space-y-2 max-h-[64vh] overflow-auto pr-1">
                {items.map((it, idx) => {
                  const activeDot = idx === active;
                  return (
                    <button
                      key={it.id}
                      onClick={() => scrollTo(idx)}
                      className={[
                        "w-full flex items-center gap-3 text-left rounded-[18px] border transition p-2",
                        activeDot
                          ? "border-brand-gold/60 bg-brand-brown/5"
                          : "border-brand-brown/10 hover:border-brand-brown/25",
                      ].join(" ")}
                      title={it.title}
                    >
                      <div className="w-14 h-10 rounded-[14px] overflow-hidden border border-brand-brown/10 bg-brand-ivory flex-shrink-0">
                        <img
                          src={it.image}
                          alt={it.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          decoding="async"
                        />
                      </div>

                      <div className="min-w-0">
                        <p className="text-[10px] uppercase tracking-[0.55em] text-brand-brown/55">
                          {pad2(idx + 1)}
                        </p>
                        <p className="text-[11px] tracking-[0.12em] text-brand-brownLight truncate">
                          {it.title}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="mt-4 h-px bg-brand-brown/10" />

              <div className="mt-4 flex items-center justify-between">
                <button
                  onClick={() => scrollTo(active - 1)}
                  className="text-[10px] uppercase tracking-[0.62em] text-brand-brown/45 hover:text-brand-brown transition"
                >
                  ← Prev
                </button>
                <button
                  onClick={() => scrollTo(active + 1)}
                  className="text-[10px] uppercase tracking-[0.62em] text-brand-brown/45 hover:text-brand-brown transition"
                >
                  Next →
                </button>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* ===== HERO (new layout) ===== */}
      <section className="relative min-h-[100vh] flex items-end">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-b from-brand-ivory/30 via-brand-ivory/75 to-brand-ivory" />
          <div className="absolute inset-0 bg-gradient-to-tr from-brand-gold/10 via-transparent to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-bl from-brand-brown/5 via-transparent to-transparent" />
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 md:px-10 pb-24 md:pb-28 w-full">
          <p className="text-[10px] uppercase tracking-[0.62em] text-brand-brownLight">
            Luxury editorial / streetwear
          </p>

          <h1 className="mt-7 text-[42px] sm:text-[52px] md:text-[78px] leading-[1.02] font-serif-lux uppercase tracking-[0.22em]">
            OWL DREAM
          </h1>

          <p className="mt-10 max-w-2xl text-sm md:text-base leading-[2.05] text-brand-brownLight">
            Luxury made quiet — designed for the ones who move at night. No noise. Just presence.
          </p>

          <div className="mt-12 flex flex-wrap items-center gap-4">
            <button
              onClick={() => scrollTo(0)}
              className="px-7 py-3.5 rounded-full bg-brand-brown/5 hover:bg-brand-brown/8 transition
                         text-[10px] uppercase tracking-[0.55em] border border-brand-brown/12"
            >
              Start editorial
            </button>

            {items[0]?.slug && (
              <button
                onClick={() => navigate(`/product/${items[0].slug}`)}
                className="px-7 py-3.5 rounded-full bg-brand-gold hover:opacity-90 transition
                           text-[10px] uppercase tracking-[0.55em] text-brand-ivory"
              >
                Shop first piece
              </button>
            )}
          </div>

          <div className="mt-14 flex items-center gap-4 text-brand-brown/45">
            <span className="text-[10px] uppercase tracking-[0.55em]">Scroll</span>
            <span className="h-px w-16 bg-brand-brown/20" />
            <span className="text-[10px] uppercase tracking-[0.55em]">to enter</span>
          </div>
        </div>
      </section>

      {/* ===== CONTENT (new magazine) ===== */}
      <div className={["relative z-10", !reducedMotion ? "od-snap" : ""].join(" ")}>
        {items.map((it, i) => {
          const flip = i % 2 === 1;
          const isActive = active === i;
          const showChapter = i % 2 === 0;
          const chapter = getChapter(i);

          return (
            <React.Fragment key={it.id}>
              {/* ===== CHAPTER INTRO ===== */}
              {showChapter && (
                <section className="min-h-[72vh] flex items-center">
                  <div className="mx-auto max-w-7xl px-4 sm:px-6 md:px-10 w-full">
                    <div
                      className="max-w-3xl"
                      style={{
                        animation: reducedMotion ? undefined : "od_fade_up 650ms ease forwards",
                      }}
                    >
                      <p className="text-[10px] uppercase tracking-[0.62em] text-brand-brownLight">
                        Chapter {pad2(chapter.no)}
                      </p>

                      <h2 className="mt-8 text-[32px] sm:text-[40px] md:text-[56px] font-serif-lux uppercase tracking-[0.22em] leading-[1.05]">
                        {chapter.title}
                      </h2>

                      <p className="mt-8 text-sm md:text-base leading-[2.05] text-brand-brownLight max-w-2xl">
                        {chapter.subtitle}
                      </p>

                      <div className="mt-16 flex items-center gap-6">
                        <div className="h-px w-32 bg-brand-gold/70" />
                        <span className="text-[10px] uppercase tracking-[0.6em] text-brand-brown/45">
                          Editorial Chapter
                        </span>
                      </div>
                    </div>
                  </div>
                </section>
              )}

              {/* ===== SCENE ===== */}
              <section
                ref={(el) => (sectionsRef.current[i] = el)}
                data-index={i}
                className="relative min-h-[110vh] flex items-center"
              >
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute inset-0 bg-gradient-to-tr from-brand-gold/10 via-transparent to-transparent" />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent via-brand-ivory/35 to-brand-ivory/70" />
                </div>

                <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 md:px-10 py-20 md:py-24 w-full">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-12 items-center">
                    {/* IMAGE */}
                    <div className={`md:col-span-7 ${flip ? "md:order-2" : ""}`}>
                      <button
                        onClick={() => setOpenIndex(i)}
                        className="group block w-full text-left"
                        aria-label={`Open ${it.title}`}
                      >
                        <div className="relative overflow-hidden rounded-[28px] md:rounded-[34px] border border-brand-brown/10 bg-brand-ivory">
                          <img
                            src={it.image}
                            alt={it.title}
                            className={[
                              "w-full",
                              "h-[62vh] md:h-[78vh]",
                              fitMode === "contain" ? "object-contain" : "object-cover",
                              "transition-transform duration-[900ms] ease-out",
                              isActive ? "scale-[1.01]" : "scale-100",
                              isActive ? "group-hover:scale-[1.025]" : "",
                            ].join(" ")}
                            loading={i <= 1 ? "eager" : "lazy"}
                            decoding="async"
                          />

                          <div className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 bg-gradient-to-tr from-brand-ivory/25 via-transparent to-brand-ivory/10" />

                          <div className="absolute top-5 left-5 rounded-full bg-brand-ivory/75 backdrop-blur-md border border-brand-brown/10 px-4 py-2">
                            <span className="text-[9px] uppercase tracking-[0.48em] text-brand-brown/70">
                              Scene {pad2(i + 1)}
                            </span>
                          </div>
                        </div>
                      </button>
                    </div>

                    {/* TEXT */}
                    <div className={`md:col-span-5 ${flip ? "md:order-1" : ""}`}>
                      <p className="text-[10px] uppercase tracking-[0.62em] text-brand-brown/55">
                        {it.drop}
                      </p>

                      <h3 className="mt-6 text-[28px] sm:text-[34px] md:text-[44px] leading-[1.06] font-serif-lux uppercase tracking-[0.16em]">
                        {it.title}
                      </h3>

                      <div
                        className={[
                          "mt-6 md:mt-8",
                          "rounded-[22px] border border-brand-brown/10",
                          "bg-brand-ivory/75 backdrop-blur-md",
                          "p-5 md:p-7",
                          "transition-all duration-[850ms] ease-out",
                          isActive ? "opacity-100 translate-y-0" : "opacity-85 translate-y-1",
                        ].join(" ")}
                      >
                        <p className="text-sm leading-[2.05] text-brand-brownLight">
                          {it.caption}
                        </p>

                        <div className="mt-7 flex items-center justify-between gap-6">
                          <span className="text-[10px] uppercase tracking-[0.6em] text-brand-brown/55 line-clamp-1">
                            {chapter.title}
                          </span>

                          <button
                            onClick={() => navigate(`/product/${it.slug}`)}
                            className={[
                              "inline-flex items-center gap-3 text-[10px] uppercase tracking-[0.6em]",
                              isActive ? "text-brand-gold" : "text-brand-brown/45",
                              "transition-colors",
                              "group",
                            ].join(" ")}
                          >
                            View piece
                            <span
                              className={[
                                "w-7 h-[1px] transition-all duration-500",
                                isActive ? "bg-brand-gold group-hover:w-12" : "bg-brand-brown/25",
                              ].join(" ")}
                            />
                          </button>
                        </div>
                      </div>

                      <div className="mt-8 flex items-center justify-between">
                        <button
                          onClick={() => scrollTo(i - 1)}
                          className="text-[10px] uppercase tracking-[0.6em] text-brand-brown/45 hover:text-brand-brown transition"
                        >
                          ← Prev
                        </button>

                        <button
                          onClick={() => scrollTo(i + 1)}
                          className="text-[10px] uppercase tracking-[0.6em] text-brand-brown/45 hover:text-brand-brown transition"
                        >
                          Next →
                        </button>
                      </div>

                      {/* mobile mini dots (lightweight, no images) */}
                      <div className="md:hidden mt-10 flex items-center gap-2">
                        {items.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => scrollTo(idx)}
                            className={[
                              "h-2.5 rounded-full transition border",
                              idx === active
                                ? "w-10 bg-brand-gold border-brand-gold/60"
                                : "w-3 bg-brand-brown/10 border-brand-brown/15",
                            ].join(" ")}
                            aria-label={`Go to scene ${idx + 1}`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            </React.Fragment>
          );
        })}
      </div>

      {/* ===== FOOTER ===== */}
      <footer className="relative z-10 py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 md:px-10">
          <div className="h-px bg-brand-brown/10" />
          <div className="mt-12 flex items-center justify-between gap-6">
            <p className="text-[10px] uppercase tracking-[0.62em] text-brand-brown/55">
              End of editorial — OWL DREAM
            </p>

            <button
              onClick={() => scrollTo(0)}
              className="text-[10px] uppercase tracking-[0.62em] text-brand-brown/45 hover:text-brand-brown transition"
            >
              Back to top ↑
            </button>
          </div>
        </div>
      </footer>

      {/* ===== MOBILE INDEX DRAWER (only thumbnails ONCE) ===== */}
      {showIndex && (
        <div
          className="fixed inset-0 z-[70]"
          role="dialog"
          aria-modal="true"
          aria-label="Lookbook Index"
        >
          <div
            className="absolute inset-0 bg-brand-brown/70 backdrop-blur-md"
            onClick={() => setShowIndex(false)}
          />
          <div className="absolute inset-x-0 bottom-0 rounded-t-[28px] bg-brand-ivory border border-brand-brown/10 overflow-hidden">
            <div className="p-5 border-b border-brand-brown/10 flex items-center justify-between">
              <p className="text-[10px] uppercase tracking-[0.62em] text-brand-brownLight">
                Index
              </p>
              <button
                onClick={() => setShowIndex(false)}
                className="h-10 px-4 rounded-full border border-brand-brown/12 text-[10px] uppercase tracking-[0.55em]"
              >
                Close
              </button>
            </div>

            <div className="max-h-[65vh] overflow-auto p-5 space-y-3">
              {items.map((it, idx) => (
                <button
                  key={it.id}
                  onClick={() => {
                    setShowIndex(false);
                    scrollTo(idx);
                  }}
                  className={[
                    "w-full flex items-center gap-4 text-left rounded-[20px] border p-3 transition",
                    idx === active
                      ? "border-brand-gold/60 bg-brand-brown/5"
                      : "border-brand-brown/10 hover:border-brand-brown/25",
                  ].join(" ")}
                >
                  <div className="w-16 h-12 rounded-[16px] overflow-hidden border border-brand-brown/10 bg-brand-ivory flex-shrink-0">
                    <img
                      src={it.image}
                      alt={it.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-[0.55em] text-brand-brown/55">
                      {pad2(idx + 1)} — {it.drop}
                    </p>
                    <p className="mt-1 text-[12px] tracking-[0.12em] text-brand-brownLight truncate">
                      {it.title}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ===== MODAL (new) ===== */}
      {openItem && openIndex != null && (
        <div
          className="fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-label={openItem.title}
          onClick={() => setOpenIndex(null)}
        >
          <div className="absolute inset-0 bg-brand-brown/70 backdrop-blur-md" />
          <div className="relative w-full max-w-6xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.62em] text-brand-ivory/70">
                  {openItem.drop} — Scene {pad2(openIndex + 1)}
                </p>
                <p className="mt-2 font-serif-lux uppercase tracking-[0.16em] text-[18px] sm:text-[20px] text-brand-ivory truncate">
                  {openItem.title}
                </p>
              </div>

              <button
                ref={closeBtnRef}
                onClick={() => setOpenIndex(null)}
                className="h-10 px-4 rounded-full border border-brand-ivory/20 text-[10px] uppercase tracking-[0.62em] text-brand-ivory/80 hover:text-brand-ivory transition"
              >
                Close
              </button>
            </div>

            <div className="rounded-[28px] overflow-hidden border border-brand-ivory/15 shadow-2xl bg-brand-brown">
              <img
                src={openItem.image}
                alt={openItem.title}
                className="w-full max-h-[82vh] object-contain"
                loading="eager"
                decoding="async"
              />
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setOpenIndex((i) => (i == null ? i : clamp(i - 1, 0, items.length - 1)))}
                  className="h-11 px-5 rounded-full border border-brand-ivory/20 text-[10px] uppercase tracking-[0.62em] text-brand-ivory/80 hover:text-brand-ivory transition"
                >
                  ← Prev
                </button>
                <button
                  onClick={() => setOpenIndex((i) => (i == null ? i : clamp(i + 1, 0, items.length - 1)))}
                  className="h-11 px-5 rounded-full border border-brand-ivory/20 text-[10px] uppercase tracking-[0.62em] text-brand-ivory/80 hover:text-brand-ivory transition"
                >
                  Next →
                </button>
              </div>

              <button
                onClick={() => navigate(`/product/${openItem.slug}`)}
                className="h-11 px-6 rounded-full bg-brand-gold hover:opacity-90 transition
                           text-[10px] uppercase tracking-[0.55em] text-brand-ivory"
              >
                View piece
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
